import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Check, Star } from "lucide-react";
import { Reveal } from "./Reveal";
import { AmbientOrbs } from "./Ambient";
import { Icon } from "./Icon";
import { Carousel, CarouselContent, CarouselItem, CarouselPrevious, CarouselNext } from "@/components/ui/carousel";
import { api } from "@/lib/api";

export const Pricing = () => {
  const [plans, setPlans] = useState([]);

  useEffect(() => {
    api
      .listPricing()
      .then(({ plans }) => setPlans(plans.map((p) => ({ ...p, priceNote: p.price_note }))))
      .catch(() => {});
  }, []);

  if (plans.length === 0) return null;

  return (
  <section id="pricing" className="relative overflow-hidden bg-[rgb(238,242,250)] py-16 md:py-20" data-testid="pricing-section">
    <AmbientOrbs />
    <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
      <div className="mx-auto max-w-2xl text-center">
        <Reveal>
          <span className="inline-flex items-center rounded-full border border-emerald2-300/40 bg-emerald2-500/10 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-emerald2-600">
            Pricing
          </span>
        </Reveal>
        <Reveal delay={0.05}>
          <h2 className="mt-4 text-3xl font-semibold tracking-tight text-navy-900 md:text-4xl lg:text-5xl">
            Expert assistance for all your business compliance needs
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="mt-4 text-base text-slate-600 md:text-lg">
            Transparent, upfront pricing on our most-requested services — no hidden charges.
          </p>
        </Reveal>
      </div>

      <Reveal delay={0.1}>
        <Carousel opts={{ align: "start", loop: true }} className="mt-14" data-testid="pricing-carousel">
          <CarouselContent>
            {plans.map((p, i) => (
              <CarouselItem key={p.title} className="sm:basis-1/2 lg:basis-1/4" data-testid={`pricing-card-${i}`}>
                <div
                  className={`group relative flex h-full flex-col overflow-hidden rounded-3xl border bg-white p-6 transition-[transform,box-shadow] duration-300 hover:-translate-y-2 hover:shadow-[0_20px_44px_rgba(15,30,60,0.12)] ${
                    p.badge ? "border-gold-300/60 shadow-[0_8px_30px_rgba(0,0,0,0.06)]" : "border-slate-200 shadow-[0_8px_30px_rgba(0,0,0,0.04)]"
                  }`}
                >
                  {p.badge && (
                    <span
                      className={`absolute inset-x-0 top-0 py-1.5 text-center text-[11px] font-bold uppercase tracking-wide text-white ${
                        p.badge === "Most Popular" ? "bg-navy-900" : "bg-emerald2-600"
                      }`}
                    >
                      {p.badge}
                    </span>
                  )}

                  <div className={`flex items-start justify-between ${p.badge ? "mt-6" : ""}`}>
                    <span className="grid h-12 w-12 place-items-center rounded-2xl bg-navy-900/5 text-navy-900">
                      <Icon name={p.icon} className="h-6 w-6" />
                    </span>
                    <div className="text-right">
                      <p className="text-[11px] font-medium text-slate-400">Starting from</p>
                      <p className="text-lg font-bold text-navy-900">
                        ₹{p.price}
                        <span className="ml-1 text-xs font-medium text-slate-400">{p.priceNote}</span>
                      </p>
                    </div>
                  </div>

                  <h3 className="mt-5 text-lg font-semibold text-navy-900">{p.title}</h3>

                  <div className="mt-1.5 flex items-center gap-1.5">
                    <div className="flex gap-0.5">
                      {Array.from({ length: 5 }).map((_, s) => (
                        <Star key={s} className="h-3.5 w-3.5 fill-gold-500 text-gold-500" />
                      ))}
                    </div>
                    <span className="text-xs font-medium text-slate-500">({p.rating})</span>
                  </div>

                  <ul className="mt-4 flex-1 space-y-2">
                    {p.features.map((f) => (
                      <li key={f} className="flex items-start gap-2 text-sm text-slate-600">
                        <Check className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald2-600" />
                        {f}
                      </li>
                    ))}
                  </ul>

                  <Link
                    to={p.slug ? `/blogs/${p.slug}` : "/contact"}
                    data-testid={`pricing-cta-${i}`}
                    className={`mt-6 inline-flex items-center justify-center gap-1.5 rounded-full px-5 py-2.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.02] ${
                      p.badge
                        ? "bg-gold-500 text-navy-950 shadow-[0_10px_24px_rgba(212,162,76,0.35)]"
                        : "border border-navy-900/15 text-navy-900 hover:border-gold-400 hover:text-gold-600"
                    }`}
                  >
                    Know More
                    <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </Link>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
          <div className="mt-8 flex items-center justify-center gap-3">
            <CarouselPrevious className="static h-10 w-10 translate-y-0" data-testid="pricing-prev" />
            <CarouselNext className="static h-10 w-10 translate-y-0" data-testid="pricing-next" />
          </div>
        </Carousel>
      </Reveal>
    </div>
  </section>
  );
};
